import { DateTime } from "luxon";

export type PeriodMode = "week" | "month" | "ytd" | "custom";

export type PeriodWindow = {
  mode: PeriodMode;
  anchor: DateTime;
  start: DateTime;
  end: DateTime;
  label: string;
};

// Weeks are ISO Monday–Sunday (Luxon default for startOf/endOf("week")).
// Do not switch to locale Sunday–Saturday weeks.
function startOfMode(anchor: DateTime, mode: PeriodMode): DateTime {
  if (mode === "week") return anchor.startOf("week");
  if (mode === "month") return anchor.startOf("month");
  return anchor.startOf("year");
}

function endOfMode(anchor: DateTime, mode: PeriodMode): DateTime {
  if (mode === "week") return anchor.endOf("week");
  // Current month/year are month-to-date / year-to-date through today.
  if (mode === "month") {
    return DateTime.min(anchor.endOf("month"), DateTime.now().endOf("day"));
  }
  return DateTime.min(anchor.endOf("year"), DateTime.now().endOf("day"));
}

function ytdLabel(start: DateTime): string {
  const year = start.toFormat("yyyy");
  // Current year is year-to-date; prior years are complete calendar years.
  if (start.year === DateTime.now().year) return `YTD ${year}`;
  return year;
}

function monthLabel(start: DateTime): string {
  const monthYear = start.toFormat("MMM yyyy");
  // Current month is month-to-date; prior months are complete calendar months.
  if (start.hasSame(DateTime.now(), "month")) return `MTD ${monthYear}`;
  return monthYear;
}

export function buildCustomPeriod(startInput: DateTime, endInput: DateTime): PeriodWindow {
  const now = DateTime.now().endOf("day");
  let start = DateTime.min(startInput.startOf("day"), now);
  let end = DateTime.min(endInput.endOf("day"), now);
  if (end < start) {
    end = start.endOf("day");
  }

  const label = `${start.toFormat("MMM d, yyyy")} – ${end.toFormat("MMM d, yyyy")}`;
  return { mode: "custom", anchor: start, start, end, label };
}

export function buildPeriod(mode: PeriodMode, anchorInput: DateTime = DateTime.now()): PeriodWindow {
  if (mode === "custom") {
    const end = DateTime.min(anchorInput.endOf("day"), DateTime.now().endOf("day"));
    const start = anchorInput.startOf("day");
    return buildCustomPeriod(start, end);
  }
  const nowAnchor = startOfMode(DateTime.now(), mode);
  let anchor = startOfMode(anchorInput, mode);
  if (anchor > nowAnchor) {
    anchor = nowAnchor;
  }
  const start = startOfMode(anchor, mode);
  const end = endOfMode(anchor, mode);

  let label: string;
  if (mode === "week") {
    label = `${start.toFormat("MMM d")} – ${end.toFormat("MMM d, yyyy")}`;
  } else if (mode === "month") {
    label = monthLabel(start);
  } else {
    label = ytdLabel(start);
  }

  return { mode, anchor, start, end, label };
}

export function shiftPeriod(period: PeriodWindow, direction: -1 | 1): PeriodWindow {
  if (period.mode === "custom") return period;
  if (period.mode === "week") {
    return buildPeriod("week", period.anchor.plus({ weeks: direction }));
  }
  if (period.mode === "month") {
    return buildPeriod("month", period.anchor.plus({ months: direction }));
  }
  return buildPeriod("ytd", period.anchor.plus({ years: direction }));
}

export function canShiftForward(period: PeriodWindow): boolean {
  if (period.mode === "custom") return false;
  const next = shiftPeriod(period, 1);
  return next.anchor > period.anchor;
}

export function currentPeriod(mode: PeriodMode): PeriodWindow {
  return buildPeriod(mode, DateTime.now());
}

export function isCurrentPeriod(period: PeriodWindow): boolean {
  if (period.mode === "custom") return false;
  const unit = period.mode === "week" ? "week" : period.mode === "month" ? "month" : "year";
  return period.anchor.hasSame(DateTime.now(), unit);
}

export function neighboringPeriods(period: PeriodWindow, count = 2): PeriodWindow[] {
  if (period.mode === "custom") return [period];
  const before: PeriodWindow[] = [];
  let current = period;
  for (let i = 0; i < count; i += 1) {
    current = shiftPeriod(current, -1);
    before.unshift(current);
  }

  const after: PeriodWindow[] = [];
  current = period;
  for (let i = 0; i < count; i += 1) {
    const next = shiftPeriod(current, 1);
    if (next.anchor <= current.anchor) break;
    after.push(next);
    current = next;
  }

  return [...before, period, ...after];
}

/** Shorter filmstrip chip text for narrow viewports; full `period.label` stays for a11y. */
export type FilmstripLabelDensity = "full" | "compact" | "narrow";

export function filmstripLabel(period: PeriodWindow, density: FilmstripLabelDensity = "full"): string {
  if (density === "full") return period.label;

  if (period.mode === "week") {
    const sameMonth = period.start.hasSame(period.end, "month");
    if (sameMonth) return `${period.start.toFormat("MMM d")}–${period.end.toFormat("d")}`;
    return `${period.start.toFormat("MMM d")}–${period.end.toFormat("MMM d")}`;
  }

  if (period.mode === "month") {
    const isCurrentMonth = period.start.hasSame(DateTime.now(), "month");
    if (density === "narrow") {
      return isCurrentMonth
        ? `MTD ${period.start.toFormat("MMM")}`
        : `${period.start.toFormat("MMM")} '${period.start.toFormat("yy")}`;
    }
    const month = period.start.toFormat("MMM yyyy");
    return isCurrentMonth ? `MTD ${month}` : month;
  }

  return period.label;
}

export function parsePeriodFromSearch(search: URLSearchParams): PeriodWindow {
  const modeParam = search.get("mode");
  if (modeParam === "custom") {
    const startParam = search.get("start");
    const endParam = search.get("end");
    const defaultEnd = DateTime.now().endOf("day");
    const defaultStart = defaultEnd.minus({ days: 29 }).startOf("day");
    const start = startParam ? DateTime.fromISO(startParam) : defaultStart;
    const end = endParam ? DateTime.fromISO(endParam) : defaultEnd;
    return buildCustomPeriod(start.isValid ? start : defaultStart, end.isValid ? end : defaultEnd);
  }

  const mode: PeriodMode = modeParam === "week" || modeParam === "ytd" ? modeParam : "month";
  const anchorParam = search.get("anchor");
  const anchor = anchorParam ? DateTime.fromISO(anchorParam) : DateTime.now();
  return buildPeriod(mode, anchor.isValid ? anchor : DateTime.now());
}

export function periodToSearch(period: PeriodWindow): URLSearchParams {
  const params = new URLSearchParams();
  params.set("mode", period.mode);
  if (period.mode === "custom") {
    params.set("start", period.start.toISODate() ?? period.start.toFormat("yyyy-MM-dd"));
    params.set("end", period.end.toISODate() ?? period.end.toFormat("yyyy-MM-dd"));
  } else {
    params.set("anchor", period.anchor.toISODate() ?? period.anchor.toFormat("yyyy-MM-dd"));
  }
  return params;
}

/** Anchor to use when changing period mode so Year never inherits a prior-year month/week. */
export function anchorForModeChange(mode: PeriodMode, current: PeriodWindow): DateTime {
  if (mode === "ytd") return DateTime.now();
  if (mode === "custom") return current.start;
  return current.anchor;
}

export function customPeriodFromWindow(period: PeriodWindow): PeriodWindow {
  return buildCustomPeriod(period.start, period.end);
}
